import { OrderedSet, Set } from 'immutable';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ViewModelService } from 'src/app/shared/view-model.service';
import { WaypointType } from 'src/graphql/generated/graphql.generated';
import { OperationViewModel } from 'src/models/operation-view-model';
import { TripLegViewModel } from 'src/models/trip-leg-view-model';
import { TripPassengerViewModel } from 'src/models/trip-passenger-view-model';
import { selectManifestEntriesByTripId } from 'src/store/entityStores/manifest-selectors';
import {
  selectTripPassengersById,
  selectTripPassengersByTripId,
} from 'src/store/entityStores/trip-passenger-selectors';
import {
  selectCurrentOrNextTripLeg,
  selectEnRouteTripLeg,
  selectNonCancelledTripLegsByTripId,
  selectTripLegAfterTripLegId,
  selectTripLegById,
} from 'src/store/entityStores/tripleg-selectors';
import { selectFirstWaypointByTripAndType } from 'src/store/entityStores/waypoint-selectors';
import { TripLegRecord } from 'src/store/generated/trip-leg-record.generated';
import { TripRecord, TripViewModelBase } from 'src/store/generated/trip-record.generated';
import { undefinedOrSwitchMap } from 'src/util/rxjs/operators';
import { selectPlannedTripLeg } from '../store/entityStores/tripleg-selectors';

export class TripViewModel extends TripViewModelBase {
  public readonly operation: OperationViewModel;

  get firstTripLeg$(): Observable<TripLegViewModel | undefined> {
    return this._model.store.select(selectNonCancelledTripLegsByTripId(this.id)).pipe(
      map((legs) => {
        if (legs == null || legs.isEmpty()) {
          return undefined;
        }
        return legs.first() as TripLegRecord;
      }),
      switchMap((leg) =>
        leg == null ? of(undefined as TripLegViewModel | undefined) : this.getLegViewModel(leg),
      ),
    );
  }

  get legIds$(): Observable<OrderedSet<string>> {
    return this._model.store.select(selectNonCancelledTripLegsByTripId(this.id)).pipe(
      map((legs) => {
        return legs;
      }),
      map((legs) =>
        legs
          .sort((l) => l.sequence)
          .map((l) => l.id)
          .toOrderedSet(),
      ),
    );
  }

  get passengerIds$(): Observable<Set<string>> {
    return combineLatest([
      this._model.store.select(selectTripPassengersByTripId(this.id)),
      this._model.store.select(selectManifestEntriesByTripId(this.id)),
    ]).pipe(
      map(([passengers, manifests]) =>
        passengers
          .filter((p) => manifests.find((m) => m.passengerId === p.id) != null)
          .map((p) => p.id)
          .toSet(),
      ),
    );
  }
  get allPassengerIds$(): Observable<Set<string>> {
    return this._model.store
      .select(selectTripPassengersByTripId(this.id))
      .pipe(map((passengers) => passengers.map((p) => p.id).toSet()));
  }
  get currentOrNextTripLeg$() {
    return this._model.store
      .select(selectCurrentOrNextTripLeg(this.id))
      .pipe(switchMap((tripLeg) => (tripLeg != null ? this.getLeg(tripLeg.id) : of(undefined))));
  }

  public getWaypointByType(type: WaypointType) {
    return this._model.store
      .select(selectFirstWaypointByTripAndType(this.id, type))
      .pipe(switchMap((wp) => (wp != null ? this._model.getWaypointById(wp.id) : of(undefined))));
  }

  constructor(trip: TripRecord, operation: OperationViewModel, private _model: ViewModelService) {
    super(trip);
    this.operation = operation;
  }

  public getCurrentTripLeg() {
    return this._model.store
      .select(selectEnRouteTripLeg(this.id))
      .pipe(switchMap((leg) => (leg == null ? of(undefined) : this.getLegViewModel(leg))));
  }

  public getNextTripLeg() {
    return this._model.store
      .select(selectPlannedTripLeg(this.id))
      .pipe(switchMap((leg) => (leg == null ? of(undefined) : this.getLegViewModel(leg))));
  }

  public getTripLegAfterTripLegId(tripLegId: string) {
    return this._model.store
      .select(selectTripLegAfterTripLegId(this.id, tripLegId))
      .pipe(undefinedOrSwitchMap((leg) => this.getLegViewModel(leg)));
  }

  public getPassenger(id: string): Observable<TripPassengerViewModel | null> {
    return this._model.store.select(selectTripPassengersById(id)).pipe(
      switchMap((passenger) => {
        if (passenger == null) {
          return of(null as TripPassengerViewModel | null);
        }
        return this._model.getPersonById(passenger.personId).pipe(
          map((maybePerson) => {
            return new TripPassengerViewModel(passenger, this, maybePerson, this._model);
          }),
        );
      }),
    );
  }

  public getLeg(id: string) {
    return this._model.store.select(selectTripLegById(id)).pipe(
      map((leg) => {
        if (leg == null || leg.tripId !== this.id) {
          return undefined;
        }
        return leg;
      }),
      switchMap((leg) =>
        leg == null ? of(undefined as TripLegViewModel | undefined) : this.getLegViewModel(leg),
      ),
      // tap(() => console.log('getting trip leg'))
    );
  }

  public getLegViewModel(leg: TripLegRecord): Observable<TripLegViewModel> {
    const startingWaypoint$ =
      leg.startingWaypointId != null
        ? this._model.getWaypointById(leg.startingWaypointId)
        : of(undefined);
    const endingWaypoint$ =
      leg.endingWaypointId != null
        ? this._model.getWaypointById(leg.endingWaypointId)
        : of(undefined);

    return combineLatest([startingWaypoint$, endingWaypoint$]).pipe(
      map(([startingWaypoint, endingWaypoint]) => {
        return new TripLegViewModel(leg, this, startingWaypoint, endingWaypoint, this._model);
      }),
    );
  }
}
